import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './AuthContext';
import { canManageTeam } from './roles';
import FlameIcon from './FlameIcon';
import { API_BASE_URL } from './config';
import { CARD, PRIMARY_BTN } from './ui';
import DateRangeFilter from './dashboard/DateRangeFilter';
import StatTile from './dashboard/StatTile';
import ActivityChart from './dashboard/ActivityChart';
import BarList from './dashboard/BarList';
import TeamActivity from './dashboard/TeamActivity';
import AttentionPanel from './dashboard/AttentionPanel';
import RecentActivity from './dashboard/RecentActivity';
import { DEFAULT_RANGE, rangeQuery, formatDay, formatRangeLabel } from './dashboard/range';
import { formatCount, formatCompact, percentChange } from './dashboard/theme';

const SUBTITLES = {
  inspector: 'Your submission activity at a glance.',
  manager: 'How your inspection team is tracking.',
  admin: 'Submission activity across your company.',
  dev: 'Submission activity across every company.',
};

const Home = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState(DEFAULT_RANGE);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const query = rangeQuery(range);

  const fetchDashboard = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/dashboard?${query}`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load dashboard:', err);
      setError(err.response?.data?.msg || 'Could not load dashboard metrics.');
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  const showTeam = canManageTeam(user.role);
  const isDev = user.role === 'dev';

  const totals = data?.totals;
  const days = data?.range?.days ?? 7;
  const comparisonLabel = `vs previous ${days} day${days === 1 ? '' : 's'}`;
  const rangeLabel = data ? formatRangeLabel(data.range.start, data.range.end) : '';

  const idleInspectors = (data?.by_user || [])
    .filter((entry) => entry.role === 'inspector' && entry.count === 0 && !entry.must_change_password)
    .map((entry) => ({ key: `idle-${entry.user_id}`, label: entry.email }));
  const pendingActivation = (data?.pending_activation || []).map((entry) => ({
    key: `pending-${entry.user_id}`,
    label: entry.email,
  }));
  const unusedForms = (data?.by_form || [])
    .filter((form) => form.count === 0)
    .map((form) => ({ key: `form-${form.form_id}`, label: form.name }));

  const usedForms = (data?.by_form || []).filter((form) => form.count > 0);
  const reworkRate = totals?.submissions ? Math.round((totals.resubmissions / totals.submissions) * 100) : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-800 via-gray-800 to-gray-900 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white mb-2">Dashboard</h1>
          <p className="text-lg text-gray-400">{SUBTITLES[user.role] || SUBTITLES.inspector}</p>
        </div>

        {/* One filter row, scoping everything below it. */}
        <div className="mb-8">
          <DateRangeFilter
            range={range}
            onChange={setRange}
            resolvedStart={data?.range?.start}
            resolvedEnd={data?.range?.end}
            busy={loading && Boolean(data)}
          />
        </div>

        {error && (
          <div className="rounded-xl border border-red-500/30 bg-red-500/10 px-6 py-4 text-red-300 mb-8">
            {error}
          </div>
        )}

        {!data && loading && (
          <div className={`${CARD} py-20 text-center text-gray-400`}>Loading metrics…</div>
        )}

        {data && totals.all_time === 0 && (
          <div className={`${CARD} py-16 px-4 text-center`}>
            <FlameIcon className="mx-auto w-16 h-16 mb-6" />
            <h2 className="text-xl font-semibold text-white mb-2">No submissions yet</h2>
            <p className="text-gray-400 mb-6 max-w-md mx-auto">
              {showTeam
                ? 'Once your team starts submitting forms, this page will track volume, per-person activity, and coverage gaps.'
                : 'Submit your first form and your activity will show up here.'}
            </p>
            <button type="button" onClick={() => navigate('/forms')} className={PRIMARY_BTN}>
              Go to Forms
            </button>
          </div>
        )}

        {data && totals.all_time > 0 && (
          // Refetch holds the previous render rather than flashing a skeleton.
          <div className={`space-y-6 transition-opacity duration-200 ${loading ? 'opacity-60' : 'opacity-100'}`}>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              <StatTile
                hero
                label="Submissions"
                value={formatCount(totals.submissions)}
                change={percentChange(totals.submissions, totals.previous_submissions)}
                comparisonLabel={comparisonLabel}
                caption={`${formatCompact(totals.all_time)} all time`}
              />
              {showTeam ? (
                <StatTile
                  label="Active submitters"
                  value={formatCount(totals.active_submitters)}
                  comparisonLabel={`of ${formatCount(totals.roster_size)} team members`}
                  meter={{ value: totals.active_submitters, total: totals.roster_size }}
                  caption={
                    totals.roster_size
                      ? `${Math.round((totals.active_submitters / totals.roster_size) * 100)}% of the team submitted`
                      : null
                  }
                />
              ) : (
                <StatTile
                  label="Busiest day"
                  value={totals.busiest_day ? formatDay(totals.busiest_day.date) : '—'}
                  comparisonLabel={
                    totals.busiest_day ? `${formatCount(totals.busiest_day.count)} submissions` : 'No activity yet'
                  }
                />
              )}
              <StatTile
                label="Average per day"
                value={totals.avg_per_day}
                comparisonLabel={`over ${days} day${days === 1 ? '' : 's'}`}
                caption={
                  totals.busiest_day
                    ? `Peak ${formatCount(totals.busiest_day.count)} on ${formatDay(totals.busiest_day.date)}`
                    : 'No submissions in this period'
                }
              />
              <StatTile
                label="Re-submissions"
                value={formatCount(totals.resubmissions)}
                comparisonLabel={`${reworkRate}% of submissions`}
                caption="Forms filed again against an earlier submission."
              />
            </div>

            <ActivityChart daily={data.daily} subtitle={rangeLabel} />

            {showTeam && (
              <div className="grid gap-6 lg:grid-cols-3">
                <div className="lg:col-span-2">
                  <TeamActivity
                    entries={data.by_user}
                    currentUserId={user.id}
                    showCompany={isDev}
                    comparisonLabel={comparisonLabel}
                  />
                </div>
                <AttentionPanel
                  idleInspectors={idleInspectors}
                  pendingActivation={pendingActivation}
                  unusedForms={unusedForms}
                  rangeLabel={rangeLabel}
                />
              </div>
            )}

            <div className="grid gap-6 lg:grid-cols-3">
              <BarList
                title="Form usage"
                subtitle="Which forms your submissions came from."
                items={usedForms.map((form) => ({
                  key: form.form_id,
                  label: form.name,
                  value: form.count,
                  meta: isDev ? form.company_name : null,
                }))}
                emptyLabel="No forms were submitted in this period."
                // Catalog coverage only means something to whoever owns the
                // catalog; an inspector's "forms available" is just the forms
                // they happened to submit, so the ratio would be a tautology.
                footer={
                  showTeam && totals.forms_available > 0
                    ? `${formatCount(totals.forms_used)} of ${formatCount(totals.forms_available)} forms used`
                    : null
                }
                onSelect={(item) => navigate(`/forms/${item.key}/fill`)}
              />

              {isDev && (
                <BarList
                  title="By company"
                  subtitle="Submission volume per customer."
                  items={data.by_company.map((company) => ({
                    key: company.company_id ?? 'unassigned',
                    label: company.name,
                    value: company.count,
                    meta: `${formatCount(company.submitters)} active submitter${company.submitters === 1 ? '' : 's'}`,
                  }))}
                  emptyLabel="No company submitted in this period."
                />
              )}

              <div className={isDev ? '' : 'lg:col-span-2'}>
                <RecentActivity items={data.recent} showSubmitter={showTeam} />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Home;
